import { Services } from '../../types/services';

// Action Types
const MSC_SET_FILTER = 'MSC_SET_FILTER';
const MSC_SET_PIMS_FILTER = 'MSC_SET_PIMS_FILTER';
const MSC_SET_CURRENT_PAGE = 'MSC_SET_CURRENT_PAGE';
const MSC_SET_INCLUDE_INACTIVE_PLUGINS = 'MSC_SET_INCLUDE_INACTIVE_PLUGINS';
const MSC_SET_EDITMODAL_ACTIVE_PLUGIN = 'MSC_SET_EDITMODAL_ACTIVE_PLUGIN';
const MSC_SET_EDITMODAL_ACTIVE_PLUGIN_ID = 'MSC_SET_EDITMODAL_ACTIVE_PLUGIN_ID';
const MSC_SET_EDITMODAL_PLUGIN_STATUS = 'MSC_SET_EDITMODAL_PLUGIN_STATUS';
const MSC_SET_PLUGIN_ISINACTIVE = 'MSC_SET_PLUGIN_ISINACTIVE';
const MSC_SET_SELECTED_PLUGIN_DEFAULTVERSION = 'MSC_SET_SELECTED_PLUGIN_DEFAULTVERSION';
const MSC_SET_EDITMODAL_PLUGIN_DEPENDENCIES = 'MSC_SET_EDITMODAL_PLUGIN_DEPENDENCIES';
const MSC_REMOVE_EDITMODAL_PLUGIN_DEPENDENCY = 'MSC_REMOVE_EDITMODAL_PLUGIN_DEPENDENCY';
const MSC_SOURCESTATE_EDITMODAL_PLUGIN_DEPENDENCIES = 'MSC_SOURCESTATE_EDITMODAL_PLUGIN_DEPENDENCIES';
const MSC_SET_EDITMODAL_PIMS_DEPENDENCIES = 'MSC_SET_EDITMODAL_PIMS_DEPENDENCIES';
const MSC_REMOVE_EDITMODAL_PIMS_DEPENDENCY = 'MSC_REMOVE_EDITMODAL_PIMS_DEPENDENCY';
const MSC_SOURCESTATE_EDITMODAL_PIMS_DEPENDENCIES = 'MSC_SOURCESTATE_EDITMODAL_PIMS_DEPENDENCIES';
const MSC_SET_EDITMODAL_PLUGIN_VERSION = 'MSC_SET_EDITMODAL_PLUGIN_VERSION';
const MSC_UPDATE_EDITMODAL_PLUGIN_VERSION = 'MSC_UPDATE_EDITMODAL_PLUGIN_VERSION';
const MSC_REMOVE_EDITMODAL_PLUGIN_VERSION = 'MSC_REMOVE_EDITMODAL_PLUGIN_VERSION';
const MSC_SOURCESTATE_EDITMODAL_PLUGIN_VERSION = 'MSC_SOURCESTATE_EDITMODAL_PLUGIN_VERSION';

// Interfaces
interface PluginDependency {
    plugins: string;
    [key: string]: unknown;
}

interface PimsDependency {
    pims: string;
    pimsVersion: string;
    [key: string]: unknown;
}

interface PluginVersion {
    pluginId: string;
    version: string;
    [key: string]: unknown;
}

interface EditModalPluginStatus {
    detailPlugin: boolean;
    addNewPlugin: boolean;
    addDependencies: boolean;
    editingPlugin: boolean;
    editingVersions: boolean;
}

interface State {
    pluginFilter: string;
    pimsFilter: string;
    includeInactivePlugins: boolean;
    isPluginInactive: boolean;
    pluginDependencies: PluginDependency[];
    pimsDependencies: PimsDependency[];
    pluginVersions: PluginVersion[];
    selectedDefaultVersion: string;
    sourcepluginVersions: PluginVersion[];
    sourcepluginDependencies: PluginDependency[];
    sourcepimsDependencies: PimsDependency[];
    editCurrentPlugin: Record<string, unknown>;
    editCurrentPluginId: string;
    editModalPluginStatus: EditModalPluginStatus;
}

interface Action {
    type: string;
    payload?: unknown;
}

const initialState: State = {
    pluginFilter: '',
    pimsFilter: '',
    includeInactivePlugins: false,
    isPluginInactive: false,
    pluginDependencies: [],
    pimsDependencies: [],
    pluginVersions: [],
    selectedDefaultVersion: '',
    sourcepluginVersions: [],
    sourcepluginDependencies: [],
    sourcepimsDependencies: [],
    editCurrentPlugin: {},
    editCurrentPluginId: '',
    editModalPluginStatus: {
        detailPlugin: false,
        addNewPlugin: false,
        addDependencies: false,
        editingPlugin: false,
        editingVersions: false,
    },
};

export const reducer = (state = initialState, action: Action): State => {
    switch (action.type) {
        case MSC_SET_CURRENT_PAGE:
            return state;
        case MSC_SET_FILTER:
            return {
                ...state,
                pluginFilter: action.payload as string,
            };
        case MSC_SET_PIMS_FILTER:
            return {
                ...state,
                pimsFilter: action.payload as string,
            };
        case MSC_SET_INCLUDE_INACTIVE_PLUGINS:
            return {
                ...state,
                includeInactivePlugins: action.payload as boolean,
            };
        case MSC_SET_PLUGIN_ISINACTIVE:
            return {
                ...state,
                isPluginInactive: action.payload as boolean,
            };
        case MSC_SET_EDITMODAL_PLUGIN_DEPENDENCIES:
            return {
                ...state,
                pluginDependencies: [
                    ...state.pluginDependencies,
                    action.payload as PluginDependency,
                ],
            };
        case MSC_REMOVE_EDITMODAL_PLUGIN_DEPENDENCY:
            return {
                ...state,
                pluginDependencies: state.pluginDependencies.filter(
                    (item) => item.plugins !== action.payload
                ),
            };
        case MSC_SOURCESTATE_EDITMODAL_PLUGIN_DEPENDENCIES:
            return {
                ...state,
                sourcepluginDependencies: [
                    ...state.sourcepluginDependencies,
                    action.payload as PluginDependency,
                ],
            };
        case MSC_SET_EDITMODAL_PIMS_DEPENDENCIES:
            return {
                ...state,
                pimsDependencies: [
                    ...state.pimsDependencies,
                    action.payload as PimsDependency,
                ],
            };
        case MSC_REMOVE_EDITMODAL_PIMS_DEPENDENCY: {
            const pimsData = action.payload as PimsDependency | undefined;
            let newpimsDependencies = state.pimsDependencies;

            if (pimsData?.pims.trim() !== '' && pimsData?.pimsVersion.trim() !== '') {
                newpimsDependencies = state.pimsDependencies.filter(
                    (item) =>
                        item.pims !== pimsData?.pims ||
                        item.pimsVersion !== pimsData?.pimsVersion
                );
            } else if (pimsData?.pims.trim() !== '') {
                newpimsDependencies = state.pimsDependencies.filter(
                    (item) => item.pims !== pimsData?.pims
                );
            }

            return {
                ...state,
                pimsDependencies: newpimsDependencies,
            };
        }
        case MSC_SOURCESTATE_EDITMODAL_PIMS_DEPENDENCIES:
            return {
                ...state,
                sourcepimsDependencies: [
                    ...state.sourcepimsDependencies,
                    action.payload as PimsDependency,
                ],
            };
        case MSC_SET_EDITMODAL_PLUGIN_VERSION:
            return {
                ...state,
                pluginVersions: [
                    ...state.pluginVersions,
                    action.payload as PluginVersion,
                ],
            };
        case MSC_UPDATE_EDITMODAL_PLUGIN_VERSION: {
            const payloadVersion = action.payload as PluginVersion;
            const pluginsUpdated = state.pluginVersions.map((plugin) => {
                if (
                    plugin.pluginId === payloadVersion.pluginId &&
                    plugin.version === payloadVersion.version
                ) {
                    return { ...plugin, ...payloadVersion };
                }
                return plugin;
            });

            return {
                ...state,
                pluginVersions: pluginsUpdated,
            };
        }
        case MSC_REMOVE_EDITMODAL_PLUGIN_VERSION: {
            const versionData = action.payload as PluginVersion | undefined;
            return {
                ...state,
                pluginVersions: state.pluginVersions.filter(
                    (item) =>
                        item.pluginId !== versionData?.pluginId ||
                        item.version !== versionData?.version
                ),
            };
        }
        case MSC_SOURCESTATE_EDITMODAL_PLUGIN_VERSION:
            return {
                ...state,
                sourcepluginVersions: [
                    ...state.sourcepluginVersions,
                    action.payload as PluginVersion,
                ],
            };
        case MSC_SET_EDITMODAL_ACTIVE_PLUGIN:
            return {
                ...state,
                editCurrentPlugin: action.payload as Record<string, unknown>,
            };
        case MSC_SET_EDITMODAL_ACTIVE_PLUGIN_ID:
            return {
                ...state,
                editCurrentPluginId: action.payload as string,
            };
        case MSC_SET_EDITMODAL_PLUGIN_STATUS:
            return {
                ...state,
                editModalPluginStatus: action.payload as EditModalPluginStatus,
            };
        case MSC_SET_SELECTED_PLUGIN_DEFAULTVERSION:
            return {
                ...state,
                selectedDefaultVersion: action.payload as string,
            };
        default:
            return state;
    }
};

export const actions = (services: Services) => {
    const axiosCall = services.axios.Axios();

    const setCurrentPage = (currentPage: number) => {
        return {
            type: MSC_SET_CURRENT_PAGE,
            payload: currentPage,
        };
    };

    const setFilter = (filter: string) => {
        return {
            type: MSC_SET_FILTER,
            payload: filter,
        };
    };

    const setPimsFilter = (pimsfilter: string) => {
        return {
            type: MSC_SET_PIMS_FILTER,
            payload: pimsfilter,
        };
    };

    const setIncludeInactivePlugins = (value = false) => {
        return {
            type: MSC_SET_INCLUDE_INACTIVE_PLUGINS,
            payload: value,
        };
    };

    const getPlugin = (id: string) => async () => {
        return axiosCall.get('missioncontrol/plugins/' + id);
    };

    const getPlugins =
        (
            includeTestPlugins = false,
            includeInactivePlugins = false,
            filter = '',
            pimsFilter = ''
        ) =>
        async () => {
            const params: Record<string, unknown> = {};
            if (filter !== '') params.pluginfilter = filter;
            if (pimsFilter !== '') params.pimsFilter = pimsFilter;
            if (includeTestPlugins === true) params.includeTestPlugins = true;
            if (includeInactivePlugins === true)
                params.includeInactivePlugins = true;

            return axiosCall.get('missionControl/plugins', { params });
        };

    const getPluginsByOffering = (offering: string) => async () => {
        return axiosCall.get('missioncontrol/plugins/offering/', {
            params: { offering: offering },
        });
    };

    const getActivePlugins = () => async () => {
        try {
            const response = await axiosCall.get<{ data: unknown }>('missioncontrol/plugins/active');
            return response.data;
        } catch (err) {
            console.error(err);
        }
    };

    const getActiveNonOfferingPlugins = (offering: string) => async () => {
        try {
            const response = await axiosCall.get<{ data: unknown }>(
                'missioncontrol/plugins/active/nonoffering/',
                { params: { offering: offering } }
            );
            return response.data;
        } catch (err) {
            console.error(err);
        }
    };

    const getPluginVersions = (pluginId: string) => async () => {
        try {
            const response = await axiosCall.get<{ data: unknown }>(
                'missioncontrol/plugins/versions/' + pluginId
            );
            return response.data;
        } catch (err) {
            console.error(err);
        }
    };

    const getPluginDependenciesPlugins = (pluginId: string) => async () => {
        try {
            const response = await axiosCall.get<{ data: unknown }>(
                'missioncontrol/plugins/dependencies/plugins/' + pluginId
            );
            return response.data;
        } catch (err) {
            console.error(err);
        }
    };

    const getPluginDependenciesPims = (pluginId: string) => async () => {
        try {
            const response = await axiosCall.get<{ data: unknown }>(
                'missioncontrol/plugins/dependencies/pims/' + pluginId
            );
            return response.data;
        } catch (err) {
            console.error(err);
        }
    };

    const setEditModalPluginStatus = (status?: EditModalPluginStatus) => {
        if (!status)
            status = {
                detailPlugin: false,
                addNewPlugin: false,
                addDependencies: false,
                editingPlugin: false,
                editingVersions: false,
            };
        return {
            type: MSC_SET_EDITMODAL_PLUGIN_STATUS,
            payload: status,
        };
    };

    const setPluginIsInactive = (isInactive: boolean) => {
        return {
            type: MSC_SET_PLUGIN_ISINACTIVE,
            payload: isInactive,
        };
    };

    const setPluginDependencies = (pluginDependencies: PluginDependency) => {
        return {
            type: MSC_SET_EDITMODAL_PLUGIN_DEPENDENCIES,
            payload: pluginDependencies,
        };
    };

    const setSourcePluginDependencies = (pluginDependencies: PluginDependency) => {
        return {
            type: MSC_SOURCESTATE_EDITMODAL_PLUGIN_DEPENDENCIES,
            payload: pluginDependencies,
        };
    };

    const removePluginDependency = (pluginName: string) => {
        return {
            type: MSC_REMOVE_EDITMODAL_PLUGIN_DEPENDENCY,
            payload: pluginName,
        };
    };

    const setPimsDependencies = (pimsDependencies: PimsDependency) => {
        return {
            type: MSC_SET_EDITMODAL_PIMS_DEPENDENCIES,
            payload: pimsDependencies,
        };
    };

    const setSourcePimsDependencies = (pimsDependencies: PimsDependency) => {
        return {
            type: MSC_SOURCESTATE_EDITMODAL_PIMS_DEPENDENCIES,
            payload: pimsDependencies,
        };
    };

    const removePimsDependency = (pimsData: PimsDependency) => {
        return {
            type: MSC_REMOVE_EDITMODAL_PIMS_DEPENDENCY,
            payload: pimsData,
        };
    };

    const setPluginVersions = (versions: PluginVersion) => {
        return {
            type: MSC_SET_EDITMODAL_PLUGIN_VERSION,
            payload: versions,
        };
    };

    const setSourcePluginVersions = (pluginVersions: PluginVersion) => {
        return {
            type: MSC_SOURCESTATE_EDITMODAL_PLUGIN_VERSION,
            payload: pluginVersions,
        };
    };

    const updatePluginVersion = (version: PluginVersion) => {
        return {
            type: MSC_UPDATE_EDITMODAL_PLUGIN_VERSION,
            payload: version,
        };
    };

    const removePluginVersion = (version: PluginVersion) => {
        return {
            type: MSC_REMOVE_EDITMODAL_PLUGIN_VERSION,
            payload: version,
        };
    };

    const setEditModalCurrentPlugin = (plugin: Record<string, unknown> = {}) => {
        return {
            type: MSC_SET_EDITMODAL_ACTIVE_PLUGIN,
            payload: plugin,
        };
    };

    const setEditModalCurrentPluginId = (id: string) => {
        return {
            type: MSC_SET_EDITMODAL_ACTIVE_PLUGIN_ID,
            payload: id,
        };
    };

    const setSelectedPluginDefaultVersion = (defaultVersion: string) => {
        return {
            type: MSC_SET_SELECTED_PLUGIN_DEFAULTVERSION,
            payload: defaultVersion,
        };
    };

    const addOrUpdatePlugin = (plugin: Record<string, unknown>) => async () => {
        try {
            await axiosCall.put('MissionControl/plugin', plugin);
            return {
                isSuccess: true,
                message: 'Plugin update succeeded!',
            };
        } catch (error) {
            return {
                isSuccess: false,
                message: 'Plugin update failed!',
            };
        }
    };

    const addPluginToOffering = (pluginId: string, offeringName: string) => async () => {
        try {
            const endpoint = `MissionControl/plugins/offering?offeringName=${offeringName}&pluginId=${pluginId}`;
            const response = await axiosCall.post(endpoint);
            return {
                payload: {
                    pluginId: pluginId,
                    offeringName: offeringName,
                    offeringId: response,
                },
            };
        } catch (error) {
            return {
                isSuccess: false,
                message: 'Failed to add plugin to offering!',
            };
        }
    };

    const removePluginFromOffering = (pluginId: string, offeringName: string) => async () => {
        try {
            const endpoint = `MissionControl/plugins/offering?offeringName=${offeringName}&pluginId=${pluginId}`;
            const response = await axiosCall.delete(endpoint);
            return {
                payload: {
                    pluginId: pluginId,
                    offeringName: offeringName,
                    offeringId: response,
                },
            };
        } catch (error) {
            return {
                isSuccess: false,
                message: 'Failed to remove plugin from offering!',
            };
        }
    };

    const fetchMissionControlVersions = () => async () => {
        try {
            const response = await axiosCall.get<{ data: unknown }>(
                'MissionControl/missionControlVersion/all'
            );
            return response.data;
        } catch (err) {
            console.error(err);
        }
    };

    const fetchDefaultMissionControlVersion = () => async () => {
        try {
            const response = await axiosCall.get<{ data: unknown }>(
                'MissionControl/missionControlVersion/default'
            );
            return response.data;
        } catch (err) {
            console.error(err);
        }
    };

    const setDefaultMissionControlVersion = (version: string) => {
        return async () => {
            try {
                await axiosCall.patch(
                    `MissionControl/missionControlVersion/default?versionString=${version}`
                );
                return {
                    isSuccess: true,
                    message: 'Default Mission Control version updated!',
                };
            } catch (err) {
                console.error(err);
                return {
                    isSuccess: false,
                    message: 'Failed to update default Mission Control version!',
                };
            }
        };
    };

    return {
        getActivePlugins,
        getActiveNonOfferingPlugins,
        getPlugin,
        getPlugins,
        getPluginsByOffering,
        getPluginVersions,
        getPluginDependenciesPlugins,
        getPluginDependenciesPims,
        setFilter,
        setPimsFilter,
        setCurrentPage,
        setIncludeInactivePlugins,
        setPluginIsInactive,
        setSelectedPluginDefaultVersion,
        setPluginDependencies,
        setPimsDependencies,
        setSourcePluginDependencies,
        setSourcePimsDependencies,
        setSourcePluginVersions,
        setPluginVersions,
        setEditModalCurrentPlugin,
        setEditModalCurrentPluginId,
        setEditModalPluginStatus,
        removePluginDependency,
        removePimsDependency,
        removePluginVersion,
        updatePluginVersion,
        addOrUpdatePlugin,
        addPluginToOffering,
        removePluginFromOffering,
        fetchMissionControlVersions,
        fetchDefaultMissionControlVersion,
        setDefaultMissionControlVersion,
    };
};

